import React from 'react';
import { LineChart, BarChart2, Briefcase } from 'lucide-react';

/**
 * Компонент с дополнительными опциями для калькулятора инфляции
 */
const AdvancedOptionsSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  categoryInflationMultipliers
}) => {
  // Получаем множитель для выбранной категории
  const multiplier = categoryInflationMultipliers[formData.itemCategory] || 1.0;
  const multiplierText = multiplier === 1.0 
    ? "равна" 
    : (multiplier > 1.0 ? `выше в ${multiplier.toFixed(1)} раз` : `ниже в ${(1/multiplier).toFixed(1)} раз`);

  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-red-500" />
        Дополнительные параметры
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        {/* Вывод информации о коэффициенте категории */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
          <div className="flex items-start">
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <span className="font-medium">Инфляция для категории "{getReadableCategoryName(formData.itemCategory)}"</span> {multiplierText} общей инфляции
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Разные категории товаров и услуг имеют разные темпы роста цен
              </p>
            </div>
          </div>
        </div>

        {/* Опции визуализации */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Параметры визуализации
          </div>
          <div className="space-y-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeChart"
                checked={formData.includeChart}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Показать график изменения стоимости
              </span>
            </label>
            
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeHistorical"
                checked={formData.includeHistorical}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Показать историческую инфляцию
              </span>
            </label>
          </div>
        </div>

        {/* Сравнение с активами */}
        <div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="compareToBenchmarks"
              checked={formData.compareToBenchmarks}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Сравнить с инвестиционными активами
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-6">
            Покажет как изменилась бы стоимость при вложении в различные активы
          </p>
        </div>

        {/* Учет налогов */}
        <div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="considerTaxes"
              checked={formData.considerTaxes}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать налогообложение
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-6">
            Расчет НДФЛ с инвестиционного дохода (для режима "Рост стоимости")
          </p>
        </div>

        {/* Компаундинг */}
        <div>
          <label htmlFor="compoundingPeriods" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Briefcase className="w-4 h-4 mr-2 text-red-500" />
            Частота начисления
          </label>
          <select
            id="compoundingPeriods"
            name="compoundingPeriods"
            value={formData.compoundingPeriods}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="1">Ежегодное (1 раз в год)</option>
            <option value="2">Полугодовое (2 раза в год)</option>
            <option value="4">Квартальное (4 раза в год)</option>
            <option value="12">Ежемесячное (12 раз в год)</option>
            <option value="365">Ежедневное (непрерывное)</option>
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Влияет на точность расчета сложного процента при длительных периодах
          </p>
        </div>

        {/* Регион */}
        <div>
          <label htmlFor="selectedRegion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Регион
          </label>
          <select
            id="selectedRegion"
            name="selectedRegion"
            value={formData.selectedRegion}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="russia">Россия (средние показатели)</option>
            <option value="moscow">Москва</option>
            <option value="spb">Санкт-Петербург</option>
            <option value="central">Центральный ФО</option>
            <option value="northwest">Северо-Западный ФО</option>
            <option value="south">Южный ФО</option>
            <option value="volga">Приволжский ФО</option>
            <option value="ural">Уральский ФО</option>
            <option value="siberia">Сибирский ФО</option>
            <option value="fareast">Дальневосточный ФО</option>
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            В данной версии калькулятора используются средние показатели по России
          </p>
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для преобразования ID категории в читаемое название
const getReadableCategoryName = (categoryId) => {
  const categoryNames = {
    general: "Общая инфляция",
    food: "Продукты питания",
    housing: "Жилье и коммунальные услуги",
    transport: "Транспорт",
    medical: "Медицинские услуги",
    education: "Образование",
    clothing: "Одежда и обувь",
    entertainment: "Развлечения",
    electronics: "Электроника",
    furniture: "Мебель и бытовая техника"
  };
  
  return categoryNames[categoryId] || categoryId;
};

export default AdvancedOptionsSection;